import React, {useContext, useEffect, useState} from "react";
import {ButtonType, UiButton} from "../button/uiButton";
import {useTranslation} from "react-i18next";
import Cropper from "react-easy-crop";
import {getCroppedImg, Position} from "./cropImage";
import {AppContext} from "../../authProvider/AuthProvider";
import {ToastParam, ToastType} from "../toast/UiToast";

type UiCropImageType = {
    file: File | null | undefined,
    cancelFunc?: () => void,
    confirmFunc?: (file: File) => void
}

export const UiCropImage: React.FC<UiCropImageType> = (props) => {
    const { t } = useTranslation()
    
    const [imageUrl, setImageUrl] = useState<string>("")
    const [crop, setCrop] = useState<Position>({ x: 0, y: 0 })
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [zoom, setZoom] = useState(1)
    const { openToastWithParams } = useContext(AppContext)

    const onCropComplete = (_: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels)
    }

    useEffect(() => {
        if (props.file) {
            setImageUrl(URL.createObjectURL(props.file))
        }
    }, [props.file]);
    
    async function returnImage() {
        if (props.confirmFunc === undefined) {
            return;
        }

        let croppedImage: File | null = null;
        try {
            croppedImage = await getCroppedImg(
                imageUrl,
                croppedAreaPixels
            )
        } catch (_) {
            const toastParam: ToastParam = {
                type: ToastType.error,
                message: t("error.error-while-cropping-image"),
            }
            openToastWithParams(toastParam)
            return;
        }

        if (!croppedImage) {
            return;
        }
        
        props.confirmFunc(croppedImage);
    }
    
    return (
        <div className="h-full w-full flex flex-col justify-end">            
            <div className="h-4/5">
                <Cropper
                    classes={{containerClassName: "h-4/5"}}
                    image={imageUrl}
                    zoom={zoom}
                    onZoomChange={setZoom}
                    aspect={1}
                    crop={crop}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                />
            </div>

            <div className="flex justify-center">
                <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setZoom(+e.target.value)
                    }}
                    className="min-w-24 w-1/3 bg-transparent cursor-pointer appearance-none disabled:opacity-50 disabled:pointer-events-none focus:outline-none
                      [&::-webkit-slider-thumb]:w-2.5
                      [&::-webkit-slider-thumb]:h-2.5
                      [&::-webkit-slider-thumb]:-mt-0.5
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:bg-white
                      [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(37,99,235,1)]
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:transition-all
                      [&::-webkit-slider-thumb]:duration-150
                      [&::-webkit-slider-thumb]:ease-in-out
                      [&::-webkit-slider-thumb]:dark:bg-white
                    
                      [&::-moz-range-thumb]:w-2.5
                      [&::-moz-range-thumb]:h-2.5
                      [&::-moz-range-thumb]:appearance-none
                      [&::-moz-range-thumb]:bg-white
                      [&::-moz-range-thumb]:border-4
                      [&::-moz-range-thumb]:border-blue-600
                      [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:transition-all
                      [&::-moz-range-thumb]:duration-150
                      [&::-moz-range-thumb]:ease-in-out
                    
                      [&::-webkit-slider-runnable-track]:w-full
                      [&::-webkit-slider-runnable-track]:h-2
                      [&::-webkit-slider-runnable-track]:bg-gray-100
                      [&::-webkit-slider-runnable-track]:rounded-full
                      [&::-webkit-slider-runnable-track]:dark:bg-white
                    
                      [&::-moz-range-track]:w-full
                      [&::-moz-range-track]:h-2
                      [&::-moz-range-track]:bg-gray-100
                      [&::-moz-range-track]:rounded-full"
                />
            </div>

            <div className="flex justify-end items-end mt-3 gap-2">
                <UiButton
                    type={ButtonType.transparentBg}
                    text={t("button.cancel")}
                    onClick={props.cancelFunc}
                />

                <UiButton
                    text={t("button.confirm")}
                    onClick={async () => {
                        await returnImage()
                    }}
                />
            </div>
        </div>
    )
}
